import {Injectable} from '@angular/core';

import { forkJoin, Subject } from 'rxjs';

import {Driver} from '@commons/models/domain/Driver';
import { WorkerHttpService } from '../http-services/worker-http.service';
import { DriverLicesnseStatus } from '@src/app/enum/driver-license.enum';

export enum ViewWorkers {
  Active = 1,
  Archive = 2,
}

export interface IGetListDataWorkersParams {
  view: ViewWorkers;
  pagination: {
    pageIndex: number;
    rows: number;
  };
  searchValue: string;
  filterDriverLicenseStatuses: DriverLicesnseStatus[];
}

export interface IGetListDataWorkersResponse {
  totalRecordsInList: number;
  listData: { data: Driver, isChecked: boolean }[];
}

@Injectable({
  providedIn: 'root'
})
export class WorkerService {

  newUnsavedDriver?: Driver;
  archivedDrivers: Driver[] = [];
  allWorkers: Driver[] = [];

  archivedWorkerSubject = new Subject<Driver>();
  unArchivedWorkerSubject = new Subject<Driver>();
  workerAddedSubject = new Subject<Driver>();
  workerUpdatedSubject = new Subject<Driver>();

  workerDetailsPanelClosedSub$ = new Subject<boolean>();

  private lastFilteredWorkersSnapshot: {
    data: Driver[];
    searchValue: string;
  };

  constructor(
    private workerHttpService: WorkerHttpService,
  ) {}

  public get getAllFilteredList(): Driver[] {
    return this.lastFilteredWorkersSnapshot?.data || [];
  }

  public getListData({ view, pagination, searchValue, filterDriverLicenseStatuses }: IGetListDataWorkersParams): IGetListDataWorkersResponse {
    let listData = view === ViewWorkers.Active ? [...this.allWorkers] : [...this.archivedDrivers];
    const start = pagination.pageIndex * pagination.rows;
    const end = start + pagination.rows;

    if (searchValue?.length) {
      if (this.lastFilteredWorkersSnapshot?.searchValue === searchValue) {
        listData = [...this.lastFilteredWorkersSnapshot.data];
      }
      else {
        listData = this.filterWorkersList(listData, searchValue);
        this.lastFilteredWorkersSnapshot = {
          data: [...listData],
          searchValue,
        };
      }
    }
    else {
      this.lastFilteredWorkersSnapshot = null as any;
    }

    if (filterDriverLicenseStatuses?.length) {
      listData = listData.filter(el => el.L && filterDriverLicenseStatuses.includes(el.LS));
    }

    return {
      listData: listData.slice(start, end).map(item => ({ data: item, isChecked: false })),
      totalRecordsInList: listData?.length,
    };
  }

  public filterWorkersList(array: Driver[], searchValue: string): Driver[] {
    if (!searchValue?.length) {
      return [];
    }
    const lowerCaseSearchTerm = searchValue.toLowerCase();
    return array.filter(item => {
      return (
        item.FN?.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.LN?.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.Em?.toLowerCase().includes(lowerCaseSearchTerm) ||
        item.CN?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    });
  }

  public archiveWorkers(listWorkersToArchive: Driver[]): void {
    this.lastFilteredWorkersSnapshot = null as any;
    const archivedWorkersIds = listWorkersToArchive.map(item => item.Id);
    this.allWorkers = this.allWorkers.filter(item => !archivedWorkersIds.includes(item.Id));
    this.archivedDrivers.unshift(...listWorkersToArchive);
    this.workerHttpService.archive(null, archivedWorkersIds).subscribe();
  }

  public restoreWorkers(listOfWorkersToRestore: Driver[]): void {
    this.lastFilteredWorkersSnapshot = null as any;
    const restoreWorkersIds = listOfWorkersToRestore.map(item => item.Id);
    this.archivedDrivers = this.archivedDrivers.filter(item => !restoreWorkersIds.includes(item.Id));
    this.allWorkers.unshift(...listOfWorkersToRestore);
    const requests = restoreWorkersIds.map(id => this.workerHttpService.unarchive(id));
    forkJoin(requests).subscribe();
  }

  public addNewWorkerTolist(worker: Driver): void {
    this.allWorkers.unshift(worker);
  }

  public updateWorker(worker: Driver): void {
    const indexInList = this.allWorkers.findIndex(item => item.Id === worker.Id);
    this.allWorkers[indexInList] = {
      ...this.allWorkers[indexInList],
      ...worker,
    };
  }

}
